import axios from "axios";

/**
 * scheduleBookingService — public (no-auth) member booking API + display helpers.
 * Every endpoint here is PUBLIC and is called without a Bearer token, so the
 * booking page works for logged-out members and inside an embed.
 *
 * Requires: VITE_BASE_API_URL (e.g. https://your-api-host/api/v1)
 */

const publicHttp = axios.create({ baseURL: import.meta.env.VITE_BASE_API_URL });

const unwrap = (response) => response?.data;

export const scheduleErrorMessage = (error, fallback = "Scheduling request failed") =>
  error?.response?.data?.message ||
  error?.response?.data?.reason ||
  error?.response?.data?.detail ||
  error?.message ||
  fallback;

/**
 * Step 0 — resolve a booking slug → org + published services.
 * Response: { success, org: { id, name }, services: [{ id, name, type, ... }] }
 */
export async function resolvePublicSlug(slug) {
  return unwrap(await publicHttp.get(`/schedule/public/${slug}`));
}

/**
 * Group model — services that currently have at least one open, future session.
 */
export async function listServicesWithSessions(orgId) {
  return unwrap(await publicHttp.get("/schedule/book/services-with-sessions", { params: { orgId } }));
}

/**
 * Group model — upcoming open sessions (spotsLeft > 0), soonest first.
 * Pass serviceId to scope to one service; omit it for an org-wide list.
 */
export async function listBookableSessions(orgId, serviceId) {
  return unwrap(
    await publicHttp.get("/schedule/book/sessions", {
      params: { orgId, ...(serviceId ? { serviceId } : {}) },
    })
  );
}

/**
 * Group model — member claims a spot in a session.
 * Now requires a verified phone (same OTP gate as free-slot bookings).
 * payload: { bookingToken, member: { name, email, phone } }
 * The token's phone must match member.phone.
 */
export async function enrollInSession(sessionId, payload) {
  return unwrap(await publicHttp.post(`/schedule/book/sessions/${sessionId}/enroll`, payload));
}

/**
 * Free-slot model — bookable services (published, with a connected coach).
 */
export async function listBookableServices(orgId) {
  return unwrap(await publicHttp.get("/schedule/book/services", { params: { orgId } }));
}

/**
 * Free-slot model — live available slots inside a UTC window.
 * window: { from, to } as ISO-8601 strings.
 * Returns { service, coach, timezone, slots: [{ start_time, end_time }] }
 * where start_time / end_time are Unix SECONDS (UTC).
 */
export async function getFreeSlots(serviceId, { from, to }) {
  return unwrap(await publicHttp.get(`/schedule/book/${serviceId}/slots`, { params: { from, to } }));
}

/**
 * Free-slot model — send an SMS OTP to the booker's phone (rate-limited:
 * 3 / 10 min per phone, 15 / 10 min per IP → 429 OTP_RATE_LIMITED).
 * phone: E.164, e.g. "+15551234567"
 */
export async function startBookingOtp(phone) {
  return unwrap(await publicHttp.post("/schedule/book/otp/start", { phone }));
}

/**
 * Free-slot model — verify the SMS OTP.
 * Returns { success, phone, bookingToken, expiresIn } (expiresIn in seconds).
 * Keep bookingToken in memory only; it is a short-lived credential.
 */
export async function verifyBookingOtp(phone, otp) {
  return unwrap(await publicHttp.post("/schedule/book/otp/verify", { phone, otp }));
}

/**
 * Free-slot model — book a chosen slot.
 * payload: { startIso, bookingToken, member: { name, email, phone } }
 * bookingToken comes from verifyBookingOtp and member.phone must match the
 * phone it was issued for.
 */
export async function bookSlot(serviceId, payload) {
  return unwrap(await publicHttp.post(`/schedule/book/${serviceId}`, payload));
}

// ── Display helpers (member BROWSER tz unless an explicit IANA tz is passed) ──

/** "Thursday, Sep 25, 2026" */
export function fmtSessionDate(iso, tz) {
  if (!iso) return "";
  return new Intl.DateTimeFormat(undefined, {
    weekday: "long", month: "short", day: "numeric", year: "numeric",
    ...(tz ? { timeZone: tz } : {}),
  }).format(new Date(iso));
}

/** "Thu, Sep 25" */
export function fmtSessionDateShort(iso, tz) {
  if (!iso) return "";
  return new Intl.DateTimeFormat(undefined, {
    weekday: "short", month: "short", day: "numeric",
    ...(tz ? { timeZone: tz } : {}),
  }).format(new Date(iso));
}

/** "3:00 PM" */
export function fmtSessionTime(iso, tz) {
  if (!iso) return "";
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric", minute: "2-digit",
    ...(tz ? { timeZone: tz } : {}),
  }).format(new Date(iso));
}

/** Spots label, e.g. "8 of 12 left" (group) or "Available" (1 on 1). */
export function spotsLabel(session) {
  const capacity = session?.capacity ?? 0;
  const left = session?.spotsLeft ?? Math.max(0, capacity - (session?.enrolledCount ?? 0));
  if (session?.isFull || left <= 0) return "Full";
  if (capacity === 1) return "Available";
  return `${left} of ${capacity} left`;
}
